import { css, unsafeCSS, type CSSResult } from '@design.estate/dees-element';

/**
 * Shared code-syntax palette — system code colors.
 *
 * Single source of truth for syntax highlighting across the catalog
 * (dees-dataview-codebox, the wysiwyg code block, dees-workspace-markdownoutlet).
 * Role colors are drawn from the same system palette as the terminal ANSI
 * colors in 00theme.ts; function/comment/plain roles map onto the shared
 * label/link tokens so they track the theme automatically.
 *
 * Components include both CSSResults in their static styles:
 * `syntaxPaletteStyles` emits the `--dees-syntax-*` custom properties
 * (dark defaults on :host, light overrides on :host([gobright])), and
 * `syntaxScopeStyles` maps highlight.js 11 scope classes onto them.
 */
export interface ISyntaxPalette {
  keyword: string;
  string: string;
  number: string;
  type: string;
}

/** Dark-theme syntax role colors (system palette). */
export const syntaxPaletteDark: ISyntaxPalette = {
  keyword: '#bf5af2',
  string: '#30d158',
  number: '#ff9f0a',
  type: '#64d2ff',
};

/** Light-theme syntax role colors (accessible-on-light system palette). */
export const syntaxPaletteLight: ISyntaxPalette = {
  keyword: '#9f00a7',
  string: '#248a3d',
  number: '#b25000',
  type: '#0071a4',
};

const paletteVars = (palette: ISyntaxPalette): string =>
  Object.entries(palette)
    .map(([role, value]) => `--dees-syntax-${role}: ${value};`)
    .join('\n    ');

export const syntaxPaletteStyles: CSSResult = css`
  :host {
    ${unsafeCSS(paletteVars(syntaxPaletteDark))}
  }

  :host([gobright]) {
    ${unsafeCSS(paletteVars(syntaxPaletteLight))}
  }
`;

/** highlight.js 11 scope classes → syntax roles + shared theme tokens. */
export const syntaxScopeStyles: CSSResult = css`
  .hljs-keyword,
  .hljs-literal,
  .hljs-selector-tag,
  .hljs-doctag,
  .hljs-template-tag {
    color: var(--dees-syntax-keyword);
  }

  .hljs-string,
  .hljs-regexp,
  .hljs-addition {
    color: var(--dees-syntax-string);
  }

  .hljs-number,
  .hljs-symbol,
  .hljs-bullet,
  .hljs-meta,
  .hljs-link {
    color: var(--dees-syntax-number);
  }

  .hljs-built_in,
  .hljs-type,
  .hljs-title.class_,
  .hljs-selector-class,
  .hljs-selector-id,
  .hljs-selector-pseudo,
  .hljs-selector-attr,
  .hljs-attr,
  .hljs-attribute,
  .hljs-property,
  .hljs-variable.language_ {
    color: var(--dees-syntax-type);
  }

  .hljs-title,
  .hljs-section,
  .hljs-name {
    color: var(--dees-color-link);
  }

  .hljs-comment,
  .hljs-quote {
    color: var(--dees-color-text-secondary);
  }

  .hljs-variable,
  .hljs-template-variable,
  .hljs-subst,
  .hljs-operator,
  .hljs-punctuation,
  .hljs-params {
    color: var(--dees-color-text-primary);
  }

  .hljs-deletion {
    color: var(--dees-color-text-error);
  }

  .hljs-emphasis {
    font-style: italic;
  }

  .hljs-strong {
    font-weight: 600;
  }
`;
